import { useState, useEffect, useMemo, useRef, useCallback, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UserPlusIcon,
  TrashIcon,
  ShieldCheckIcon,
  EnvelopeIcon,
  Cog6ToothIcon,
  UserGroupIcon,
  CheckIcon,
  XMarkIcon,
  ClockIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  VideoCameraIcon,
  ClipboardDocumentIcon,
  PhotoIcon,
  ExclamationTriangleIcon,
  RectangleStackIcon,
  AdjustmentsHorizontalIcon,
  PencilSquareIcon,
  NoSymbolIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { isAxiosError } from 'axios';
import { updateMemberRole, getChannelTemplates, getCoachQuota, setCoachQuota, deleteCoachQuota, setMemberRemovalAuthorization } from '@/services/channelApi';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useAuth } from '@/contexts/AuthContext';
import {
  getChannel,
  updateChannel,
  getChannelMembers,
  removeChannelMember,
  inviteChannelMember,
  getChannelInvitations,
  revokeChannelInvitation,
  resendChannelInvitation,
  uploadChannelLogo,
  uploadChannelBanner,
} from '@/services/channelApi';
import { toggleChannelLeaderboard } from '@/services/leaderboardApi';
import type {
  Channel,
  ChannelMember,
  ChannelPendingInvitation,
  UpdateChannelRequest,
  ChannelTemplateItem,
  CoachQuotaResponse,
} from '@/types/channel';
import Input from '@/components/ui/Input';
import Spinner from '@/components/ui/Spinner';
import { ScoreCell } from '@/components/ui/ScoreCell';
import { TrendCell } from '@/components/ui/TrendCell';
import {
  useChannelAccessRequests,
  useApproveAccessRequest,
  useDenyAccessRequest,
} from '@/hooks/useChannelAccessRequests';
import { useChannelMemberships } from '@/hooks/useChannelMemberships';
import { formatDistanceToNow } from 'date-fns';
import { getErrorMessage } from '@/utils/errors';
import { formatDate } from '@/utils/formatters';
import ChannelCreditsTab from '@/components/ChannelCreditsTab';

type TabKey = 'settings' | 'members' | 'templates' | 'credits';
type SortColumn = 'name' | 'latest_score' | 'session_count' | 'last_eval_at' | 'joined_at';
type MemberFilter = 'all' | 'active' | 'inactive';

export default function ChannelManagePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Derive the viewer's effective channel role via the canonical memberships query.
  // isOwner uses channel.owner_id as an authoritative fallback so the owner is NEVER
  // locked out even if the memberships query returns empty or errors.
  const { data: memberships = [], isLoading: rolesLoading } = useChannelMemberships();

  const [activeTab, setActiveTab] = useState<TabKey>('settings');

  // Channel data
  const [channel, setChannel] = useState<Channel | null>(null);
  const [isLoadingChannel, setIsLoadingChannel] = useState(true);
  const [channelError, setChannelError] = useState<string | null>(null);

  // Access request hooks
  const { data: accessRequests = [] } = useChannelAccessRequests(slug);
  const pendingRequests = accessRequests.filter((r) => r.status === 'pending');
  const approveRequest = useApproveAccessRequest(slug ?? '');
  const denyRequest = useDenyAccessRequest(slug ?? '');

  // Settings form — existing fields
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [sportFocus, setSportFocus] = useState('');
  const [listed, setListed] = useState(false);
  // Settings form — identity fields (B-2026-0071)
  const [tagline, setTagline] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [accreditation, setAccreditation] = useState('');
  const [igHandle, setIgHandle] = useState('');
  const [ytUrl, setYtUrl] = useState('');
  const [ttHandle, setTtHandle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // Image upload state
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  // B-2026-0072: leaderboard enabled toggle
  const [leaderboardEnabled, setLeaderboardEnabled] = useState(true);
  const [isTogglingLeaderboard, setIsTogglingLeaderboard] = useState(false);

  // Copy link state
  const [linkCopied, setLinkCopied] = useState(false);

  // Credits tab toast (lightweight, mirrors the AdminPage toast contract).
  const [creditToast, setCreditToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const showCreditToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setCreditToast({ message, type });
    setTimeout(() => setCreditToast(null), 4000);
  }, []);

  // Members
  const [members, setMembers] = useState<ChannelMember[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState<'athlete' | 'coach'>('athlete');
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [addMemberError, setAddMemberError] = useState<string | null>(null);
  const [addMemberSuccess, setAddMemberSuccess] = useState<string | null>(null);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);

  // S56: capability toggle state (owner-only: can_remove_members per member)
  // Map: userId -> { loading: bool, error: string | null }
  const [capabilityToggleState, setCapabilityToggleState] = useState<
    Record<string, { loading: boolean; error: string | null }>
  >({});

  // Members sort + filter
  const [memberSort, setMemberSort] = useState<{ column: SortColumn; direction: 'asc' | 'desc' }>({
    column: 'latest_score',
    direction: 'desc',
  });
  const [memberFilter, setMemberFilter] = useState<MemberFilter>('all');
  // B-2026-0068: coaches-only toggle — hidden for solo (T1) or unknown-tier channels
  const [showCoachesOnly, setShowCoachesOnly] = useState(false);

  // Pending invitations
  const [pendingInvitations, setPendingInvitations] = useState<ChannelPendingInvitation[]>([]);
  const [revokingInvitationId, setRevokingInvitationId] = useState<string | null>(null);
  const [resendConfirmId, setResendConfirmId] = useState<string | null>(null);
  const [resendingInvitationId, setResendingInvitationId] = useState<string | null>(null);

  // B-2026-0064/0068: role-change state
  const [changingRoleUserId, setChangingRoleUserId] = useState<string | null>(null);
  const [roleChangeError, setRoleChangeError] = useState<string | null>(null);

  // Phase C — Templates tab
  const [templates, setTemplates] = useState<ChannelTemplateItem[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [templateCoachFilter, setTemplateCoachFilter] = useState<string>('');

  // Phase C — Per-coach quota (T3 only)
  // Map: coachUserId -> { data | null (no override) | 'loading' | 'error' | undefined (not yet fetched) }
  const [coachQuotas, setCoachQuotas] = useState<Record<string, CoachQuotaResponse | null | 'loading' | 'error' | undefined>>({});
  // Per-coach inline edit state: coachUserId -> { editing: bool, value: string, saving: bool, error: string | null }
  const [coachQuotaEditing, setCoachQuotaEditing] = useState<Record<string, { editing: boolean; value: string; saving: boolean; error: string | null }>>({});

  useEffect(() => {
    if (!slug) return;
    setIsLoadingChannel(true);
    getChannel(slug)
      .then((ch) => {
        setChannel(ch);
        setDisplayName(ch.display_name);
        setDescription(ch.description ?? '');
        setSportFocus(ch.sport_focus ?? '');
        setListed(ch.listed ?? false);
        // Identity fields
        setTagline(ch.tagline ?? '');
        setWebsiteUrl(ch.website_url ?? '');
        setAccreditation(ch.accreditation ?? '');
        setIgHandle(ch.social_links?.instagram ?? '');
        setYtUrl(ch.social_links?.youtube ?? '');
        setTtHandle(ch.social_links?.tiktok ?? '');
        setLogoPreview(ch.school_logo_url);
        setBannerPreview(ch.cover_image_url);
        // B-2026-0072: init leaderboard toggle from channel data
        setLeaderboardEnabled(ch.leaderboard_enabled ?? true);
      })
      .catch(() => setChannelError('Failed to load channel.'))
      .finally(() => setIsLoadingChannel(false));
  }, [slug]);

  // Phase C — load templates when templates tab is active or coach filter changes
  useEffect(() => {
    if (!slug || activeTab !== 'templates') return;
    setIsLoadingTemplates(true);
    setTemplatesError(null);
    getChannelTemplates(slug, templateCoachFilter || undefined)
      .then(setTemplates)
      .catch((err) => setTemplatesError(getErrorMessage(err)))
      .finally(() => setIsLoadingTemplates(false));
  }, [slug, activeTab, templateCoachFilter]);

  useEffect(() => {
    if (!slug || activeTab !== 'members') return;
    setIsLoadingMembers(true);
    getChannelMembers(slug)
      .then(setMembers)
      .catch(() => setMembersError('Failed to load members.'))
      .finally(() => setIsLoadingMembers(false));
    // Load pending invitations in parallel (non-blocking — swallow errors silently)
    getChannelInvitations(slug)
      .then(setPendingInvitations)
      .catch(() => {
        // Owner-only; silently ignore if permissions change or endpoint not yet deployed
      });
  }, [slug, activeTab]);

  // Phase C — fetch quota for each coach-level member when members tab loads on T3
  useEffect(() => {
    if (!slug || activeTab !== 'members' || channel?.subscription_tier !== 3) return;
    // Only trigger once members are loaded
    if (members.length === 0) return;

    const coachLevelMembers = members.filter((m) => {
      const effectiveRole = (m.member_role ?? m.role) as string;
      return (
        effectiveRole === 'coach' ||
        effectiveRole === 'channel_coach' ||
        effectiveRole === 'channel_admin' ||
        m.access_level === 'owner'
      );
    });

    coachLevelMembers.forEach((m) => {
      // Skip if already fetched or loading
      if (coachQuotas[m.user_id] !== undefined) return;
      setCoachQuotas((prev) => ({ ...prev, [m.user_id]: 'loading' }));
      getCoachQuota(slug, m.user_id)
        .then((quota) => setCoachQuotas((prev) => ({ ...prev, [m.user_id]: quota })))
        .catch((err) => {
          if (isAxiosError(err) && err.response?.status === 404) {
            // 404 = no override set; platform default (unlimited)
            setCoachQuotas((prev) => ({ ...prev, [m.user_id]: null }));
          } else {
            setCoachQuotas((prev) => ({ ...prev, [m.user_id]: 'error' }));
          }
        });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, activeTab, channel?.subscription_tier, members]);

  const refreshPendingInvitations = async () => {
    if (!slug) return;
    try {
      const invitations = await getChannelInvitations(slug);
      setPendingInvitations(invitations);
    } catch {
      // silently ignore
    }
  };

  // Derive effective channel role for the current viewer.
  // isOwner is the authoritative fallback: channel.owner_id === user.id always grants
  // access, even if the memberships query hasn't resolved or returned empty.
  const myMembership = memberships.find((m) => m.channel_slug === slug);
  const myRole = myMembership?.member_role;
  const isOwner = !!channel && !!user && channel.owner_id === user.id;
  // Platform admin — unlocks the credit deposit + tier controls on the Credits tab.
  const isAdmin = user?.role === 'admin';
  // channel_admin delegates approve/deny of join requests (S22); no other mutations.
  const canManageChannel = isOwner || myRole === 'channel_admin';

  // S56: viewer's own can_remove_members capability from the memberships summary.
  // Owner always has implicit remove capability; others need an explicit grant.
  const viewerCanRemove = isOwner || myMembership?.can_remove_members === true;

  // Guard: only owner or channel_admin may access this page.
  // The redirect is deferred until both channel AND the memberships query have resolved
  // (!rolesLoading) to prevent a flash-redirect race for a legitimate channel_admin.
  // PRESERVE: isOwner as fallback ensures the owner is never locked out.
  useEffect(() => {
    if (channel && user && !rolesLoading && !canManageChannel) {
      navigate(`/channels/${slug}`, { replace: true });
    }
  }, [channel, user, rolesLoading, canManageChannel, slug, navigate]);

  // If a channel_admin lands on the page, the settings tab is not available to them.
  // Switch to the members tab automatically so they immediately see the approval panel.
  useEffect(() => {
    if (!isOwner && canManageChannel && activeTab === 'settings') {
      setActiveTab('members');
    }
  }, [isOwner, canManageChannel, activeTab]);

  const handleSaveSettings = async (e: FormEvent) => {
    e.preventDefault();
    if (!slug) return;
    setSaveError(null);
    setSaveSuccess(false);
    setIsSaving(true);

    // Build social_links map from individual fields
    const socialLinks: Record<string, string> = {};
    if (igHandle.trim()) socialLinks.instagram = igHandle.trim();
    if (ytUrl.trim()) socialLinks.youtube = ytUrl.trim();
    if (ttHandle.trim()) socialLinks.tiktok = ttHandle.trim();

    const payload: UpdateChannelRequest = {
      display_name: displayName.trim() || undefined,
      description: description.trim() || undefined,
      sport_focus: sportFocus.trim() || undefined,
      listed,
      // Identity fields
      tagline: tagline.trim() || undefined,
      website_url: websiteUrl.trim() || undefined,
      accreditation: accreditation.trim() || undefined,
      social_links: Object.keys(socialLinks).length > 0 ? socialLinks : undefined,
    };

    try {
      const updated = await updateChannel(slug, payload);
      setChannel(updated);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(getErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  // B-2026-0072: toggle the channel leaderboard_enabled flag
  const handleLeaderboardToggle = async () => {
    if (!slug || isTogglingLeaderboard) return;
    setIsTogglingLeaderboard(true);
    try {
      const result = await toggleChannelLeaderboard(slug);
      setLeaderboardEnabled(result.leaderboard_enabled);
      setChannel((prev) =>
        prev ? { ...prev, leaderboard_enabled: result.leaderboard_enabled } : prev,
      );
    } catch {
      // best-effort; leave toggle in current state
    } finally {
      setIsTogglingLeaderboard(false);
    }
  };

  const handleLogoChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !slug) return;
    setUploadError(null);
    // Client-side size check
    if (file.size > 2 * 1024 * 1024) {
      setUploadError('Logo must be ≤ 2 MB.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setUploadError('Logo must be an image file.');
      return;
    }
    // Preview
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
    // Upload
    setIsUploadingLogo(true);
    try {
      const result = await uploadChannelLogo(slug, file);
      setLogoPreview(result.url);
      setChannel((prev) => prev ? { ...prev, school_logo_url: result.url } : prev);
    } catch {
      setUploadError('Failed to upload logo. Please try again.');
      setLogoPreview(null);
    } finally {
      setIsUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  }, [slug]);

  const handleBannerChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !slug) return;
    setUploadError(null);
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Banner must be ≤ 5 MB.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setUploadError('Banner must be an image file.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setBannerPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
    setIsUploadingBanner(true);
    try {
      const result = await uploadChannelBanner(slug, file);
      setBannerPreview(result.url);
      setChannel((prev) => prev ? { ...prev, cover_image_url: result.url } : prev);
    } catch {
      setUploadError('Failed to upload banner. Please try again.');
      setBannerPreview(null);
    } finally {
      setIsUploadingBanner(false);
      if (bannerInputRef.current) bannerInputRef.current.value = '';
    }
  }, [slug]);

  const handleCopyLink = useCallback(async () => {
    if (!channel) return;
    const url = `https://www.pointetracer.com/channels/${channel.slug}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  }, [channel]);

  const handleAddMember = async (e: FormEvent) => {
    e.preventDefault();
    if (!slug || !newMemberEmail.trim()) return;
    setAddMemberError(null);
    setAddMemberSuccess(null);
    setIsAddingMember(true);

    try {
      const result = await inviteChannelMember(slug, newMemberEmail.trim(), undefined, newMemberRole);
      if (result.outcome === 'added_existing' && result.member) {
        setMembers((prev) => [...prev, result.member!]);
        setAddMemberSuccess(
          `Added ${result.member.display_name ?? result.email} to the channel.`,
        );
      } else if (result.outcome === 'invitation_sent') {
        setAddMemberSuccess(
          `Invitation sent to ${result.email}. They'll be added automatically when they register.`,
        );
        void refreshPendingInvitations();
      }
      setNewMemberEmail('');
      setNewMemberRole('athlete');
    } catch (err) {
      setAddMemberError(getErrorMessage(err));
    } finally {
      setIsAddingMember(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!slug || removingMemberId) return;
    setRemovingMemberId(userId);
    try {
      await removeChannelMember(slug, userId);
      setMembers((prev) => prev.filter((m) => m.user_id !== userId));
    } catch {
      // In a real app, show an error toast here
    } finally {
      setRemovingMemberId(null);
    }
  };

  /**
   * S56: Toggle the can_remove_members capability for a non-owner member.
   * Owner viewer only. Updates local member state and invalidates the members cache.
   */
  const handleToggleRemovalCapability = async (userId: string, currentValue: boolean) => {
    if (!slug) return;
    const newValue = !currentValue;
    setCapabilityToggleState((prev) => ({
      ...prev,
      [userId]: { loading: true, error: null },
    }));
    try {
      await setMemberRemovalAuthorization(slug, userId, newValue);
      // Update local member state so the toggle reflects the new value immediately
      setMembers((prev) =>
        prev.map((m) =>
          m.user_id === userId ? { ...m, can_remove_members: newValue } : m,
        ),
      );
      // Invalidate the ['channel-members', slug] query key so any other hooks stay fresh
      void queryClient.invalidateQueries({ queryKey: ['channel-members', slug] });
      setCapabilityToggleState((prev) => ({
        ...prev,
        [userId]: { loading: false, error: null },
      }));
    } catch (err) {
      const detail =
        isAxiosError(err) && err.response?.data?.detail
          ? String(err.response.data.detail)
          : 'Failed to update capability. Please try again.';
      setCapabilityToggleState((prev) => ({
        ...prev,
        [userId]: { loading: false, error: detail },
      }));
    }
  };

  /**
   * B-2026-0064/0068: Change a member's per-channel role.
   * Surfaces 409 "seat limit reached" as a user-facing error.
   */
  const handleRoleChange = async (userId: string, newRole: 'athlete' | 'coach' | 'viewer') => {
    if (!slug || changingRoleUserId) return;
    setChangingRoleUserId(userId);
    setRoleChangeError(null);
    try {
      const updated = await updateMemberRole(slug, userId, { member_role: newRole });
      setMembers((prev) =>
        prev.map((m) =>
          m.user_id === userId
            ? { ...m, member_role: updated.member_role ?? newRole, role: updated.role ?? m.role }
            : m,
        ),
      );
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        setRoleChangeError(
          'Seat limit reached. Upgrade your plan or remove an existing coach seat before adding another.',
        );
      } else {
        setRoleChangeError('Failed to update role. Please try again.');
      }
    } finally {
      setChangingRoleUserId(null);
    }
  };

  // Phase C — per-coach quota handlers (T3 only)
  const openQuotaEditor = (coachId: string) => {
    const current = coachQuotas[coachId];
    const currentValue =
      current && current !== 'loading' && current !== 'error'
        ? String(current.templates_created_per_month)
        : '';
    setCoachQuotaEditing((prev) => ({
      ...prev,
      [coachId]: { editing: true, value: currentValue, saving: false, error: null },
    }));
  };

  const cancelQuotaEditor = (coachId: string) => {
    setCoachQuotaEditing((prev) => ({
      ...prev,
      [coachId]: { editing: false, value: '', saving: false, error: null },
    }));
  };

  const saveCoachQuota = async (coachId: string) => {
    if (!slug) return;
    const editorState = coachQuotaEditing[coachId];
    if (!editorState) return;
    const parsed = parseInt(editorState.value, 10);
    if (isNaN(parsed) || parsed < 0) {
      setCoachQuotaEditing((prev) => ({
        ...prev,
        [coachId]: { ...editorState, error: 'Enter a valid number (0 or more).' },
      }));
      return;
    }
    setCoachQuotaEditing((prev) => ({
      ...prev,
      [coachId]: { ...editorState, saving: true, error: null },
    }));
    try {
      const result = await setCoachQuota(slug, coachId, { templates_created_per_month: parsed });
      setCoachQuotas((prev) => ({ ...prev, [coachId]: result }));
      setCoachQuotaEditing((prev) => ({
        ...prev,
        [coachId]: { editing: false, value: '', saving: false, error: null },
      }));
    } catch (err) {
      setCoachQuotaEditing((prev) => ({
        ...prev,
        [coachId]: { ...editorState, saving: false, error: getErrorMessage(err) },
      }));
    }
  };

  const clearCoachQuota = async (coachId: string) => {
    if (!slug) return;
    try {
      await deleteCoachQuota(slug, coachId);
      setCoachQuotas((prev) => ({ ...prev, [coachId]: null }));
    } catch (err) {
      // 404 = already removed; treat as success (revert to default)
      if (isAxiosError(err) && err.response?.status === 404) {
        setCoachQuotas((prev) => ({ ...prev, [coachId]: null }));
      }
      // other errors: silently ignore for now (no blocking modal needed)
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    if (revokingInvitationId || !slug) return;
    setRevokingInvitationId(invitationId);
    try {
      await revokeChannelInvitation(slug, invitationId);
      setPendingInvitations((prev) =>
        prev.filter((inv) => inv.invitation_id !== invitationId),
      );
    } catch {
      // silently ignore — a future iteration can show a toast
    } finally {
      setRevokingInvitationId(null);
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    if (resendingInvitationId || !slug) return;
    setResendConfirmId(null);
    setResendingInvitationId(invitationId);
    try {
      const newInv = await resendChannelInvitation(slug, invitationId);
      // Replace the old row with the fresh invitation from the backend.
      setPendingInvitations((prev) =>
        prev.map((inv) => (inv.invitation_id === invitationId ? newInv : inv)),
      );
    } catch {
      // silently ignore — a future iteration can show a toast
    } finally {
      setResendingInvitationId(null);
    }
  };

  // Derived: filtered then sorted member list
  const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;

  const filteredMembers = useMemo(() => {
    if (memberFilter === 'active') {
      return members.filter(
        (m) => m.last_eval_at != null && new Date(m.last_eval_at).getTime() >= thirtyDaysAgo,
      );
    }
    if (memberFilter === 'inactive') {
      return members.filter((m) => (m.session_count ?? 0) === 0);
    }
    return members;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, memberFilter]);

  const sortedMembers = useMemo(() => {
    const col = memberSort.column;
    const dir = memberSort.direction === 'asc' ? 1 : -1;
    return [...filteredMembers].sort((a, b) => {
      if (col === 'name') {
        const aName = (a.display_name ?? a.email).toLowerCase();
        const bName = (b.display_name ?? b.email).toLowerCase();
        return dir * aName.localeCompare(bName);
      }
      if (col === 'latest_score') {
        // nulls last always
        if (a.latest_score == null && b.latest_score == null) return 0;
        if (a.latest_score == null) return 1;
        if (b.latest_score == null) return -1;
        return dir * (a.latest_score - b.latest_score);
      }
      if (col === 'session_count') {
        return dir * ((a.session_count ?? 0) - (b.session_count ?? 0));
      }
      if (col === 'last_eval_at') {
        const aT = a.last_eval_at ? new Date(a.last_eval_at).getTime() : 0;
        const bT = b.last_eval_at ? new Date(b.last_eval_at).getTime() : 0;
        // nulls last
        if (!aT && !bT) return 0;
        if (!aT) return 1;
        if (!bT) return -1;
        return dir * (aT - bT);
      }
      // joined_at
      return dir * (new Date(a.joined_at).getTime() - new Date(b.joined_at).getTime());
    });
  }, [filteredMembers, memberSort]);

  // B-2026-0068: apply coaches-only view after sort (pure display filter, no re-sort needed)
  const displayedMembers = useMemo(() => {
    if (!showCoachesOnly) return sortedMembers;
    return sortedMembers.filter((m) => {
      const role = (m.member_role ?? m.role) as string;
      return role === 'coach' || role === 'owner' || m.access_level === 'owner';
    });
  }, [sortedMembers, showCoachesOnly]);

  // Summary stats
  const scoredMembers = members.filter((m) => m.latest_score != null);
  const avgScore =
    scoredMembers.length > 0
      ? Math.round(scoredMembers.reduce((sum, m) => sum + (m.latest_score as number), 0) / scoredMembers.length)
      : null;
  const atRiskCount = members.filter((m) => m.latest_score != null && (m.latest_score as number) < 60).length;

  // B-2026-0064/0068: Seat counting — coaches only (athletes are unlimited)
  // Seat cap derived from the channel's subscription_tier (T1=1, T2=4, T3=25).
  // Falls back to showing no counter when tier is unknown.
  const coachMembers = members.filter(
    (m) => (m.member_role ?? m.role) === 'coach' || m.access_level === 'owner',
  );
  const coachSeatCount = coachMembers.length;
  const channelTier = channel?.subscription_tier ?? null;
  const seatLimit: number | null =
    channelTier === 1 ? 1 : channelTier === 2 ? 4 : channelTier === 3 ? 25 : null;

  // D12: read-only state from channel
  const isReadOnly = channel?.is_read_only ?? false;

  function handleSortClick(col: SortColumn) {
    setMemberSort((prev) =>
      prev.column === col
        ? { column: col, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
        : { column: col, direction: 'desc' },
    );
  }

  if (isLoadingChannel) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
        }}
      >
        <Spinner size="lg" label="Loading channel..." />
      </div>
    );
  }

  if (channelError || !channel) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: '16px',
        }}
      >
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '15px' }}>
          {channelError || 'Channel not found.'}
        </p>
        <button
          onClick={() => navigate(-1)}
          style={{
            color: 'var(--color-accent)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600,
          }}
        >
          Go back
        </button>
      </div>
    );
  }

  // Settings tab is owner-only: channel_admin gets only Members (approve/deny) and Templates (read).
  const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
    ...(isOwner
      ? [
          {
            key: 'settings' as const,
            label: 'Settings',
            icon: <Cog6ToothIcon style={{ width: '16px', height: '16px' }} />,
          },
        ]
      : []),
    {
      key: 'members',
      label: 'Members',
      icon: <UserGroupIcon style={{ width: '16px', height: '16px' }} />,
    },
    {
      key: 'templates',
      label: 'Templates',
      icon: <RectangleStackIcon style={{ width: '16px', height: '16px' }} />,
    },
    // Credits tab — channel owner or platform admin only.
    ...(isOwner || isAdmin
      ? [
          {
            key: 'credits' as const,
            label: 'Credits',
            icon: <CurrencyDollarIcon style={{ width: '16px', height: '16px' }} />,
          },
        ]
      : []),
  ];

  // Phase C — derived list of coach-level members for the template filter dropdown
  const coachLevelMembersForFilter = members.filter((m) => {
    const effectiveRole = (m.member_role ?? m.role) as string;
    return (
      effectiveRole === 'coach' ||
      effectiveRole === 'channel_coach' ||
      effectiveRole === 'channel_admin' ||
      m.access_level === 'owner'
    );
  });

  return (
    <>
      <style>{`
        @keyframes cm-fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .cm-page { animation: cm-fade-in 0.38s cubic-bezier(0.22, 1, 0.36, 1) both; }

        .cm-save-btn {
          transition: background-color 150ms ease, box-shadow 150ms ease, transform 100ms ease;
        }
        .cm-save-btn:not(:disabled):hover {
          background-color: var(--color-accent-hover) !important;
          box-shadow: 0 0 18px rgba(30, 215, 96, 0.30), 0 4px 12px rgba(0,0,0,0.2);
          transform: translateY(-1px);
        }
        .cm-save-btn:not(:disabled):active { transform: scale(0.97); }

        .cm-member-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          border-radius: 10px;
          transition: background-color 150ms ease;
        }
        .cm-member-row:hover {
          background-color: var(--color-dark-elevated-2);
        }
        .cm-member-row:hover .cm-remove-btn {
          opacity: 1;
        }
        .cm-remove-btn {
          opacity: 0;
          transition: opacity 150ms ease;
        }

        .cm-listed-row {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          padding: 14px 16px;
          border-radius: 12px;
          background-color: var(--color-dark-elevated-2);
          border: 1px solid var(--color-border-subtle);
          cursor: var(--cursor-hand);
          transition: border-color 150ms ease;
          max-width: 480px;
        }
        .cm-listed-row:hover {
          border-color: var(--color-border-strong);
        }
        .cm-toggle-switch {
          position: relative;
          flex-shrink: 0;
          width: 40px;
          height: 22px;
          margin-top: 2px;
        }
        .cm-toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
          position: absolute;
        }
        .cm-toggle-track {
          position: absolute;
          inset: 0;
          border-radius: 999px;
          cursor: var(--cursor-hand);
          transition: background-color 180ms ease;
        }

        .cm-sort-btn {
          background: none;
          border: none;
          cursor: var(--cursor-hand);
          font-size: 11px;
          font-weight: 600;
          color: var(--color-text-muted);
          text-transform: uppercase;
          letter-spacing: 0.6px;
          display: inline-flex;
          align-items: center;
          gap: 3px;
          padding: 0;
          transition: color 120ms ease;
          white-space: nowrap;
        }
        .cm-sort-btn:hover { color: var(--color-text-secondary); }
        .cm-sort-btn.active { color: var(--color-text-primary); }

        .cm-filter-chip {
          display: inline-flex;
          align-items: center;
          padding: 5px 12px;
          border-radius: 999px;
          border-width: 1px;
          border-style: solid;
          border-color: var(--color-border-subtle);
          background: none;
          font-size: 12px;
          font-weight: 600;
          color: var(--color-text-muted);
          cursor: var(--cursor-hand);
          transition: background-color 120ms ease, color 120ms ease, border-color 120ms ease;
        }
        .cm-filter-chip:hover {
          color: var(--color-text-secondary);
          border-color: var(--color-text-muted);
        }
        .cm-filter-chip.active {
          background-color: rgba(61,169,252,0.12);
          border-color: rgba(61,169,252,0.30);
          color: var(--color-accent-blue);
        }

        .cm-stat-mini {
          display: flex;
          flex-direction: column;
          gap: 2px;
          padding: 12px 16px;
          border-right-width: 1px;
          border-right-style: solid;
          border-right-color: var(--color-border-subtle);
          min-width: 0;
        }
        .cm-stat-mini:last-child {
          border-right-width: 0;
        }

        /* Mobile: hide secondary columns below 640px */
        @media (max-width: 639px) {
          .cm-col-trend,
          .cm-col-sessions,
          .cm-col-last { display: none !important; }
        }
      `}</style>

      <div className="cm-page" style={{ maxWidth: '760px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: '28px' }}>
          <button
            onClick={() => navigate(`/channels/${slug}`)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              color: 'var(--color-text-muted)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              marginBottom: '16px',
              padding: 0,
              transition: 'color 150ms ease',
            }}
            onMouseEnter={(e) =>
              ((e.currentTarget as HTMLButtonElement).style.color =
                'var(--color-text-primary)')
            }
            onMouseLeave={(e) =>
              ((e.currentTarget as HTMLButtonElement).style.color =
                'var(--color-text-muted)')
            }
          >
            <ArrowLeftIcon style={{ width: '16px', height: '16px' }} />
            Back to channel
          </button>

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
            <div>
              <h1
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 700,
                  fontSize: '22px',
                  color: 'var(--color-text-primary)',
                  margin: '0 0 4px',
                  letterSpacing: '-0.3px',
                }}
              >
                Channel Studio
              </h1>
              <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', margin: 0 }}>
                {channel.display_name}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
              <button
                type="button"
                onClick={() => navigate(`/channels/${slug}/stats`)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '7px 16px',
                  borderRadius: '999px',
                  backgroundColor: 'var(--color-dark-elevated-2)',
                  border: '1px solid var(--color-border-subtle)',
                  color: 'var(--color-text-secondary)',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'border-color 150ms ease, color 150ms ease, background-color 150ms ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-accent-blue)';
                  (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-accent-blue)';
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(61,169,252,0.08)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-border-subtle)';
                  (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-secondary)';
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-dark-elevated-2)';
                }}
              >
                <svg viewBox="0 0 16 16" fill="none" style={{ width: '14px', height: '14px' }} aria-hidden="true">
                  <rect x="1" y="9" width="3" height="5" rx="1" stroke="currentColor" strokeWidth="1.4" />
                  <rect x="6" y="5" width="3" height="9" rx="1" stroke="currentColor" strokeWidth="1.4" />
                  <rect x="11" y="1" width="3" height="13" rx="1" stroke="currentColor" strokeWidth="1.4" />
                </svg>
                View stats
              </button>
              {/* School Growth Analytics — B-2026-0076 */}
              <button
                type="button"
                onClick={() => navigate(`/channels/${slug}/analytics`)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '7px 16px',
                  borderRadius: '999px',
                  backgroundColor: 'var(--color-dark-elevated-2)',
                  border: '1px solid var(--color-border-subtle)',
                  color: 'var(--color-text-secondary)',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'border-color 150ms ease, color 150ms ease, background-color 150ms ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-accent-blue)';
                  (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-accent-blue)';
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(61,169,252,0.08)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-border-subtle)';
                  (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-secondary)';
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-dark-elevated-2)';
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" style={{ width: '14px', height: '14px' }} aria-hidden="true">
                  <path d="M3 3v18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M7 16l4-4 4 4 4-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                School Analytics
              </button>
            </div>
          </div>
        </div>

        {/* D12: Read-only banner — shown when subscription has lapsed */}
        {isReadOnly && (
          <div
            role="alert"
            data-testid="read-only-banner"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 16px',
              borderRadius: '12px',
              backgroundColor: 'rgba(244,201,93,0.09)',
              border: '1px solid rgba(244,201,93,0.28)',
              marginBottom: '20px',
            }}
          >
            <ExclamationTriangleIcon
              style={{ width: '18px', height: '18px', color: 'var(--color-accent-gold)', flexShrink: 0 }}
            />
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-accent-gold)' }}>
                This channel is read-only
              </span>
              <span style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginLeft: '6px' }}>
                Subscription lapsed. Renew to restore full access.
              </span>
            </div>
            <a
              href="/subscribe"
              style={{
                fontSize: '13px',
                fontWeight: 600,
                color: 'var(--color-accent-gold)',
                textDecoration: 'none',
                flexShrink: 0,
                padding: '5px 12px',
                borderRadius: '999px',
                backgroundColor: 'rgba(244,201,93,0.12)',
                border: '1px solid rgba(244,201,93,0.30)',
                transition: 'background-color 150ms ease',
              }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'rgba(244,201,93,0.20)')}
              onMouseLeave={(e) => ((e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'rgba(244,201,93,0.12)')}
            >
              Renew subscription
            </a>
          </div>
        )}

        {/* Tab navigation */}
        <div
          style={{
            display: 'flex',
            gap: '4px',
            marginBottom: '24px',
            borderBottom: '1px solid var(--color-border-subtle)',
            paddingBottom: '0',
          }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
                padding: '10px 16px',
                border: 'none',
                borderBottom: activeTab === tab.key
                  ? '2px solid var(--color-accent)'
                  : '2px solid transparent',
                background: 'none',
                cursor: 'pointer',
                color:
                  activeTab === tab.key
                    ? 'var(--color-text-primary)'
                    : 'var(--color-text-muted)',
                fontSize: '14px',
                fontWeight: activeTab === tab.key ? 600 : 400,
                transition: 'color 150ms ease, border-color 150ms ease',
                marginBottom: '-1px',
              }}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Settings Tab ─────────────────────────────────────────────── */}
        {activeTab === 'settings' && (
          <div
            style={{
              backgroundColor: 'var(--color-dark-card)',
              border: '1px solid var(--color-border-subtle)',
              borderRadius: '16px',
              padding: '28px 32px',
              boxShadow: 'var(--shadow-card)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
              <h2
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 600,
                  fontSize: '16px',
                  color: 'var(--color-text-primary)',
                  margin: 0,
                }}
              >
                Channel Settings
              </h2>
              {/* Copy link button */}
              <button
                type="button"
                onClick={handleCopyLink}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '7px 14px',
                  borderRadius: '999px',
                  backgroundColor: linkCopied ? 'rgba(30,215,96,0.12)' : 'var(--color-dark-elevated-2)',
                  border: linkCopied ? '1px solid rgba(30,215,96,0.3)' : '1px solid var(--color-border-strong)',
                  color: linkCopied ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 150ms ease',
                }}
              >
                {linkCopied ? (
                  <>
                    <CheckCircleIcon style={{ width: '14px', height: '14px' }} />
                    Copied!
                  </>
                ) : (
                  <>
                    <ClipboardDocumentIcon style={{ width: '14px', height: '14px' }} />
                    Copy channel link
                  </>
                )}
              </button>
            </div>

            <form onSubmit={handleSaveSettings} noValidate>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                {/* Display name */}
                <Input
                  label="Channel Name"
                  id="ch-display-name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="e.g. Elite Fencing Academy"
                  required
                  disabled={isSaving}
                />

                {/* Description */}
                <div style={{ width: '100%' }}>
                  <label
                    htmlFor="ch-description"
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'var(--color-text-secondary)',
                      marginBottom: '4px',
                    }}
                  >
                    Description
                  </label>
                  <textarea
                    id="ch-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe what your channel is about..."
                    rows={3}
                    disabled={isSaving}
                    style={{
                      width: '100%',
                      borderRadius: '10px',
                      border: '1px solid var(--color-border-subtle)',
                      backgroundColor: 'var(--color-dark-elevated-2)',
                      color: 'var(--color-text-primary)',
                      padding: '10px 12px',
                      fontSize: '14px',
                      resize: 'vertical',
                      outline: 'none',
                      fontFamily: 'var(--font-body)',
                      transition: 'border-color 180ms ease',
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) =>
                      (e.currentTarget.style.borderColor = 'var(--color-accent)')
                    }
                    onBlur={(e) =>
                      (e.currentTarget.style.borderColor = 'var(--color-border-subtle)')
                    }
                  />
                </div>

                {/* Sport focus */}
                <Input
                  label="Sport Focus"
                  id="ch-sport-focus"
                  value={sportFocus}
                  onChange={(e) => setSportFocus(e.target.value)}
                  placeholder="e.g. Fencing, Swimming, Tennis"
                  disabled={isSaving}
                />

                {/* Listed toggle */}
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'var(--color-text-secondary)',
                      marginBottom: '8px',
                    }}
                  >
                    Discovery
                  </label>

                  <label className="cm-listed-row" htmlFor="cm-listed-toggle">
                    {/* Toggle switch */}
                    <span className="cm-toggle-switch">
                      <input
                        id="cm-listed-toggle"
                        data-testid="cm-listed-toggle"
                        type="checkbox"
                        checked={listed}
                        onChange={(e) => setListed(e.target.checked)}
                        disabled={isSaving}
                      />
                      <span
                        className="cm-toggle-track"
                        style={{
                          backgroundColor: listed ? 'var(--color-accent)' : 'var(--color-border-strong)',
                        }}
                      >
                        <span
                          style={{
                            position: 'absolute',
                            top: '3px',
                            left: '3px',
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            background: 'white',
                            transition: 'transform 180ms ease',
                            transform: listed ? 'translateX(18px)' : 'translateX(0)',
                          }}
                        />
                      </span>
                    </span>

                    {/* Label + description */}
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span
                        style={{
                          display: 'block',
                          fontSize: '14px',
                          fontWeight: 600,
                          color: 'var(--color-text-primary)',
                          marginBottom: '3px',
                        }}
                      >
                        List this school publicly
                      </span>
                      <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', lineHeight: 1.55 }}>
                        {listed
                          ? 'Your school appears in discovery and has a public landing page with a "Request to join" button. No content (templates or evaluations) is ever visible to non-members.'
                          : 'Your school is completely private — reachable only by invite link. It does not appear in discovery or have a public landing page.'}
                      </span>
                    </span>
                  </label>
                </div>

                {/* ── Identity Fields ────────────────────────────────── */}
                <div style={{ borderTop: '1px solid var(--color-border-subtle)', paddingTop: '20px' }}>
                  <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 16px' }}>
                    School Identity
                  </p>

                  {/* Tagline */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <label htmlFor="ch-tagline" style={{ fontSize: '14px', fontWeight: 500, color: 'var(--color-text-secondary)' }}>
                        Tagline
                      </label>
                      <span style={{ fontSize: '12px', color: tagline.length > 110 ? 'var(--color-danger)' : 'var(--color-text-muted)' }}>
                        {tagline.length}/120
                      </span>
                    </div>
                    <input
                      id="ch-tagline"
                      type="text"
                      maxLength={120}
                      value={tagline}
                      onChange={(e) => setTagline(e.target.value)}
                      placeholder="e.g. Master your form, one rep at a time"
                      disabled={isSaving}
                      style={{ width: '100%', borderRadius: '10px', border: '1px solid var(--color-border-subtle)', backgroundColor: 'var(--color-dark-elevated-2)', color: 'var(--color-text-primary)', padding: '10px 12px', fontSize: '14px', outline: 'none', fontFamily: 'var(--font-body)', boxSizing: 'border-box', transition: 'border-color 180ms ease' }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--color-accent)')}
                      onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--color-border-subtle)')}
                    />
                  </div>

                  {/* Website URL */}
                  <div style={{ marginBottom: '16px' }}>
                    <label htmlFor="ch-website" style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: '4px' }}>
                      Website URL
                    </label>
                    <input
                      id="ch-website"
                      type="url"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                      placeholder="https://your-school.com"
                      disabled={isSaving}
                      style={{ width: '100%', borderRadius: '10px', border: '1px solid var(--color-border-subtle)', backgroundColor: 'var(--color-dark-elevated-2)', color: 'var(--color-text-primary)', padding: '10px 12px', fontSize: '14px', outline: 'none', fontFamily: 'var(--font-body)', boxSizing: 'border-box', transition: 'border-color 180ms ease' }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--color-accent)')}
                      onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--color-border-subtle)')}
                    />
                  </div>

                  {/* Accreditation */}
                  <div style={{ marginBottom: '16px' }}>
                    <label htmlFor="ch-accreditation" style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: '4px' }}>
                      Accreditation / Credential
                    </label>
                    <input
                      id="ch-accreditation"
                      type="text"
                      value={accreditation}
                      onChange={(e) => setAccreditation(e.target.value)}
                      placeholder="e.g. USAG Certified Level 5"
                      disabled={isSaving}
                      style={{ width: '100%', borderRadius: '10px', border: '1px solid var(--color-border-subtle)', backgroundColor: 'var(--color-dark-elevated-2)', color: 'var(--color-text-primary)', padding: '10px 12px', fontSize: '14px', outline: 'none', fontFamily: 'var(--font-body)', boxSizing: 'border-box', transition: 'border-color 180ms ease' }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--color-accent)')}
                      onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--color-border-subtle)')}
                    />
                  </div>

                  {/* Social links */}
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{ fontSize: '14px', fontWeight: 500, color: 'var(--color-text-secondary)', margin: '0 0 10px' }}>Social Links</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {[
                        { id: 'ig', label: 'Instagram handle', placeholder: '@yourhandle or username', value: igHandle, set: setIgHandle },
                        { id: 'yt', label: 'YouTube URL', placeholder: 'https://youtube.com/@yourchannel', value: ytUrl, set: setYtUrl },
                        { id: 'tt', label: 'TikTok handle', placeholder: '@yourhandle or username', value: ttHandle, set: setTtHandle },
                      ].map(({ id, label, placeholder, value, set }) => (
                        <div key={id} style={{ position: 'relative' }}>
                          <label htmlFor={`ch-${id}`} style={{ display: 'block', fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '3px' }}>{label}</label>
                          <input
                            id={`ch-${id}`}
                            type="text"
                            value={value}
                            onChange={(e) => set(e.target.value)}
                            placeholder={placeholder}
                            disabled={isSaving}
                            style={{ width: '100%', borderRadius: '10px', border: '1px solid var(--color-border-subtle)', backgroundColor: 'var(--color-dark-elevated-2)', color: 'var(--color-text-primary)', padding: '9px 12px', fontSize: '14px', outline: 'none', fontFamily: 'var(--font-body)', boxSizing: 'border-box', transition: 'border-color 180ms ease' }}
                            onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--color-accent)')}
                            onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--color-border-subtle)')}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* ── Image Uploads ─────────────────────────────────── */}
                <div style={{ borderTop: '1px solid var(--color-border-subtle)', paddingTop: '20px' }}>
                  <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 16px' }}>
                    Images
                  </p>

                  {uploadError && (
                    <div role="alert" style={{ borderRadius: '10px', border: '1px solid rgba(240,93,94,0.3)', backgroundColor: 'rgba(240,93,94,0.08)', padding: '10px 12px', fontSize: '13px', color: 'var(--color-danger)', marginBottom: '12px' }}>
                      {uploadError}
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    {/* Logo upload — square preview */}
                    <div>
                      <p style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-text-secondary)', margin: '0 0 8px' }}>School Logo <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>(max 2 MB)</span></p>
                      <div
                        onClick={() => logoInputRef.current?.click()}
                        style={{ width: '88px', height: '88px', borderRadius: '16px', backgroundColor: 'var(--color-dark-elevated-2)', border: '2px dashed var(--color-border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden', position: 'relative', transition: 'border-color 150ms ease' }}
                        onMouseEnter={(e) => ((e.currentTarget as HTMLDivElement).style.borderColor = 'var(--color-accent)')}
                        onMouseLeave={(e) => ((e.currentTarget as HTMLDivElement).style.borderColor = 'var(--color-border-strong)')}
                        role="button"
                        tabIndex={0}
                        aria-label="Upload school logo"
                        onKeyDown={(e) => e.key === 'Enter' && logoInputRef.current?.click()}
                      >
                        {isUploadingLogo ? (
                          <span style={{ width: '20px', height: '20px', border: '2px solid var(--color-border-strong)', borderTopColor: 'var(--color-accent)', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                        ) : logoPreview ? (
                          <img src={logoPreview} alt="Logo preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <PhotoIcon style={{ width: '24px', height: '24px', color: 'var(--color-text-muted)' }} />
                        )}
                      </div>
                      <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoChange} />
                    </div>

                    {/* Banner upload — wide preview */}
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-text-secondary)', margin: '0 0 8px' }}>Banner Image <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>(max 5 MB)</span></p>
                      <div
                        onClick={() => bannerInputRef.current?.click()}
                        style={{ height: '88px', borderRadius: '12px', backgroundColor: 'var(--color-dark-elevated-2)', border: '2px dashed var(--color-border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden', backgroundImage: bannerPreview ? `url(${bannerPreview})` : undefined, backgroundSize: 'cover', backgroundPosition: 'center', transition: 'border-color 150ms ease' }}
                        onMouseEnter={(e) => ((e.currentTarget as HTMLDivElement).style.borderColor = 'var(--color-accent)')}
                        onMouseLeave={(e) => ((e.currentTarget as HTMLDivElement).style.borderColor = 'var(--color-border-strong)')}
                        role="button"
                        tabIndex={0}
                        aria-label="Upload banner image"
                        onKeyDown={(e) => e.key === 'Enter' && bannerInputRef.current?.click()}
                      >
                        {isUploadingBanner ? (
                          <span style={{ width: '20px', height: '20px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                        ) : !bannerPreview ? (
                          <PhotoIcon style={{ width: '24px', height: '24px', color: 'var(--color-text-muted)' }} />
                        ) : null}
                      </div>
                      <input ref={bannerInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleBannerChange} />
                    </div>
                  </div>
                </div>

                {/* ── Leaderboard toggle — B-2026-0072 ── */}
                <div
                  style={{
                    padding: '16px',
                    borderRadius: '12px',
                    backgroundColor: 'var(--color-dark-elevated-2)',
                    border: '1px solid var(--color-border-subtle)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '16px',
                    flexWrap: 'wrap',
                  }}
                >
                  <div>
                    <p
                      style={{
                        margin: '0 0 2px',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--color-text-primary)',
                      }}
                    >
                      Enable leaderboard for this channel
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: '12px',
                        color: 'var(--color-text-muted)',
                        lineHeight: 1.5,
                      }}
                    >
                      When enabled, opted-in athletes appear on a ranked leaderboard. Disable to hide it from all members.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleLeaderboardToggle()}
                    disabled={isTogglingLeaderboard}
                    aria-pressed={leaderboardEnabled}
                    style={{
                      flexShrink: 0,
                      width: '44px',
                      height: '24px',
                      borderRadius: '999px',
                      border: 'none',
                      cursor: isTogglingLeaderboard ? 'not-allowed' : 'pointer',
                      backgroundColor: leaderboardEnabled ? 'var(--color-accent)' : 'var(--color-border-strong)',
                      position: 'relative',
                      transition: 'background-color 200ms ease',
                      opacity: isTogglingLeaderboard ? 0.6 : 1,
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        top: '3px',
                        left: leaderboardEnabled ? '23px' : '3px',
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        backgroundColor: 'white',
                        transition: 'left 200ms ease',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      }}
                    />
                    <span className="sr-only">{leaderboardEnabled ? 'Disable' : 'Enable'} leaderboard</span>
                  </button>
                </div>

                {/* Error / success feedback */}
                {saveError && (
                  <div
                    role="alert"
                    style={{
                      borderRadius: '10px',
                      border: '1px solid rgba(240, 93, 94, 0.3)',
                      backgroundColor: 'rgba(240, 93, 94, 0.08)',
                      padding: '12px 14px',
                      fontSize: '13px',
                      color: 'var(--color-danger)',
                    }}
                  >
                    {saveError}
                  </div>
                )}
                {saveSuccess && (
                  <div
                    role="status"
                    style={{
                      borderRadius: '10px',
                      border: '1px solid rgba(30, 215, 96, 0.25)',
                      backgroundColor: 'rgba(30, 215, 96, 0.08)',
                      padding: '12px 14px',
                      fontSize: '13px',
                      color: 'var(--color-accent)',
                    }}
                  >
                    Settings saved successfully.
                  </div>
                )}

                {/* Save button */}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="submit"
                    disabled={isSaving || !displayName.trim() || isReadOnly}
                    className="cm-save-btn"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 24px',
                      borderRadius: '10px',
                      backgroundColor: 'var(--color-accent)',
                      border: 'none',
                      color: 'var(--color-text-inverse)',
                      fontSize: '14px',
                      fontFamily: 'var(--font-heading)',
                      fontWeight: 600,
                      cursor: isSaving || !displayName.trim() ? 'not-allowed' : 'pointer',
                      opacity: isSaving || !displayName.trim() ? 0.6 : 1,
                    }}
                  >
                    {isSaving ? (
                      <>
                        <span
                          style={{
                            width: '14px',
                            height: '14px',
                            border: '2px solid rgba(0,0,0,0.2)',
                            borderTopColor: 'rgba(0,0,0,0.7)',
                            borderRadius: '50%',
                            display: 'inline-block',
                            animation: 'spin 0.7s linear infinite',
                          }}
                        />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* ── Members Tab ──────────────────────────────────────────────── */}
        {activeTab === 'members' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* B-2026-0064/0068: Seat counter banner */}
            {seatLimit !== null && (
              <div
                data-testid="seat-counter"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  backgroundColor: 'var(--color-dark-elevated-2)',
                  border: `1px solid ${coachSeatCount >= seatLimit ? 'rgba(240,93,94,0.30)' : 'var(--color-border-subtle)'}`,
                }}
              >
                <ShieldCheckIcon
                  style={{
                    width: '16px',
                    height: '16px',
                    color: coachSeatCount >= seatLimit ? 'var(--color-danger)' : 'var(--color-accent-blue)',
                    flexShrink: 0,
                  }}
                />
                <div>
                  <span
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: coachSeatCount >= seatLimit ? 'var(--color-danger)' : 'var(--color-text-primary)',
                    }}
                  >
                    Coaches: {coachSeatCount} / {seatLimit}
                  </span>
                  <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginLeft: '8px' }}>
                    {coachSeatCount >= seatLimit
                      ? 'Seat limit reached. Upgrade to add more coaches.'
                      : `${seatLimit - coachSeatCount} seat${seatLimit - coachSeatCount === 1 ? '' : 's'} available`}
                  </span>
                </div>
              </div>
            )}

            {/* B-2026-0064/0068: Role-change 409 error */}
            {roleChangeError && (
              <div
                role="alert"
                data-testid="role-change-error"
                style={{
                  padding: '12px 14px',
                  borderRadius: '10px',
                  border: '1px solid rgba(240,93,94,0.30)',
                  backgroundColor: 'rgba(240,93,94,0.08)',
                  fontSize: '13px',
                  color: 'var(--color-danger)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                }}
              >
                <ExclamationTriangleIcon style={{ width: '16px', height: '16px', flexShrink: 0, marginTop: '1px' }} />
                <span>{roleChangeError}</span>
                <button
                  type="button"
                  onClick={() => setRoleChangeError(null)}
                  style={{
                    marginLeft: 'auto',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--color-danger)',
                    padding: 0,
                    flexShrink: 0,
                  }}
                  aria-label="Dismiss error"
                >
                  <XMarkIcon style={{ width: '14px', height: '14px' }} />
                </button>
              </div>
            )}

            {/* Add member form — owner-only (inviting members is not delegated to channel_admin) */}
            {isOwner && (
            <div
              style={{
                backgroundColor: 'var(--color-dark-card)',
                border: '1px solid var(--color-border-subtle)',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: 'var(--shadow-card)',
              }}
            >
              <h2
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 600,
                  fontSize: '15px',
                  color: 'var(--color-text-primary)',
                  margin: '0 0 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <UserPlusIcon style={{ width: '17px', height: '17px', color: 'var(--color-accent)' }} />
                Add Member
              </h2>

              <form onSubmit={handleAddMember} noValidate>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <Input
                      label="Email address"
                      id="add-member-email"
                      type="email"
                      value={newMemberEmail}
                      onChange={(e) => setNewMemberEmail(e.target.value)}
                      placeholder="athlete@example.com"
                      disabled={isAddingMember}
                    />
                  </div>
                  {/* Role toggle — pre-fills the invitee's registration form;
                      the invitee can still change it at registration time. */}
                  <div style={{ flexShrink: 0 }}>
                    <label
                      htmlFor="invite-role-select"
                      style={{
                        display: 'block',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: 'var(--color-text-muted)',
                        marginBottom: '6px',
                        letterSpacing: '0.3px',
                      }}
                    >
                      Role
                    </label>
                    <select
                      id="invite-role-select"
                      value={newMemberRole}
                      onChange={(e) =>
                        setNewMemberRole(e.target.value as 'athlete' | 'coach')
                      }
                      disabled={isAddingMember}
                      aria-label="Invite as role"
                      style={{
                        height: '40px',
                        padding: '0 32px 0 12px',
                        borderRadius: '10px',
                        border: '1px solid var(--color-border-strong)',
                        backgroundColor: 'var(--color-surface-raised-3)',
                        color: 'var(--color-text-primary)',
                        fontSize: '14px',
                        fontWeight: 500,
                        cursor: isAddingMember ? 'not-allowed' : 'pointer',
                        appearance: 'none',
                        backgroundImage:
                          'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3E%3Cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3E%3C/svg%3E")',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 8px center',
                        backgroundSize: '16px',
                      }}
                    >
                      <option value="athlete">Athlete</option>
                      <option value="coach">Coach</option>
                    </select>
                  </div>
                  <button
                    type="submit"
                    disabled={isAddingMember || !newMemberEmail.trim() || isReadOnly}
                    style={{
                      height: '40px',
                      padding: '0 20px',
                      borderRadius: '10px',
                      backgroundColor: 'var(--color-accent)',
                      border: 'none',
                      color: 'var(--color-text-inverse)',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor:
                        isAddingMember || !newMemberEmail.trim()
                          ? 'not-allowed'
                          : 'pointer',
                      opacity: isAddingMember || !newMemberEmail.trim() ? 0.6 : 1,
                      flexShrink: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'background-color 150ms ease',
                    }}
                    onMouseEnter={(e) => {
                      if (!isAddingMember && newMemberEmail.trim()) {
                        (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                          'var(--color-accent-hover)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                        'var(--color-accent)';
                    }}
                  >
                    {isAddingMember ? (
                      <span
                        style={{
                          width: '14px',
                          height: '14px',
                          border: '2px solid rgba(0,0,0,0.2)',
                          borderTopColor: 'rgba(0,0,0,0.7)',
                          borderRadius: '50%',
                          display: 'inline-block',
                          animation: 'spin 0.7s linear infinite',
                        }}
                      />
                    ) : (
                      <EnvelopeIcon style={{ width: '14px', height: '14px' }} />
                    )}
                    {isAddingMember ? 'Sending...' : 'Invite'}
                  </button>
                </div>

                {addMemberError && (
                  <p
                    role="alert"
                    style={{
                      marginTop: '8px',
                      fontSize: '13px',
                      color: 'var(--color-danger)',
                    }}
                  >
                    {addMemberError}
                  </p>
                )}
                {addMemberSuccess && (
                  <p
                    role="status"
                    style={{
                      marginTop: '8px',
                      fontSize: '13px',
                      color: 'var(--color-accent)',
                    }}
                  >
                    {addMemberSuccess}
                  </p>
                )}
              </form>
            </div>
            )} {/* end isOwner — Add member form */}

            {/* Pending access requests panel */}
            {pendingRequests.length > 0 && (
              <div
                style={{
                  backgroundColor: 'var(--color-dark-card)',
                  border: '1px solid var(--color-border-subtle)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: 'var(--shadow-card)',
                }}
              >
                {/* Section header */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '16px 20px',
                    borderBottom: '1px solid var(--color-border-subtle)',
                    backgroundColor: 'var(--color-dark-elevated-1)',
                  }}
                >
                  <h2
                    style={{
                      fontFamily: 'var(--font-heading)',
                      fontWeight: 600,
                      fontSize: '15px',
                      color: 'var(--color-text-primary)',
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    Pending access requests
                  </h2>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '22px',
                      height: '22px',
                      borderRadius: '999px',
                      backgroundColor: 'rgba(244, 201, 93, 0.18)',
                      border: '1px solid rgba(244, 201, 93, 0.35)',
                      color: 'var(--color-accent-gold)',
                      fontSize: '11px',
                      fontWeight: 700,
                    }}
                  >
                    {pendingRequests.length}
                  </span>
                </div>

                {/* Request rows */}
                <div style={{ padding: '8px 0' }}>
                  {pendingRequests.map((req) => (
                    <div
                      key={req.id}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        padding: '14px 20px',
                        gap: '12px',
                        borderBottom: '1px solid var(--color-border-subtle)',
                      }}
                    >
                      {/* Requester info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p
                          style={{
                            margin: 0,
                            fontSize: '14px',
                            fontWeight: 500,
                            color: 'var(--color-text-primary)',
                          }}
                        >
                          {req.requester_display_name || req.requester_email}
                        </p>
                        {req.requester_display_name && (
                          <p
                            style={{
                              margin: '2px 0 0',
                              fontSize: '12px',
                              color: 'var(--color-text-muted)',
                            }}
                          >
                            {req.requester_email}
                          </p>
                        )}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginTop: '6px',
                            flexWrap: 'wrap',
                          }}
                        >
                          <span
                            style={{
                              fontSize: '11px',
                              padding: '2px 8px',
                              borderRadius: '999px',
                              backgroundColor: 'rgba(61,169,252,0.10)',
                              border: '1px solid rgba(61,169,252,0.20)',
                              color: 'var(--color-accent-blue)',
                              fontWeight: 500,
                            }}
                          >
                            Wants: {req.requested_role}
                          </span>
                          <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
                            {formatDistanceToNow(new Date(req.created_at), { addSuffix: true })}
                          </span>
                        </div>
                        {req.message && (
                          <p
                            style={{
                              margin: '8px 0 0',
                              fontSize: '13px',
                              color: 'var(--color-text-secondary)',
                              fontStyle: 'italic',
                              lineHeight: 1.5,
                            }}
                          >
                            "{req.message}"
                          </p>
                        )}
                      </div>

                      {/* Approve / Deny buttons */}
                      <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                        <button
                          onClick={() => approveRequest.mutate(req.id)}
                          disabled={approveRequest.isPending || denyRequest.isPending}
                          title="Approve"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            padding: '7px 14px',
                            borderRadius: '8px',
                            border: '1px solid rgba(30,215,96,0.25)',
                            backgroundColor: 'rgba(30,215,96,0.10)',
                            color: 'var(--color-accent)',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'background-color 120ms ease',
                          }}
                          onMouseEnter={(e) =>
                            ((e.currentTarget as HTMLButtonElement).style.backgroundColor =
                              'rgba(30,215,96,0.18)')
                          }
                          onMouseLeave={(e) =>
                            ((e.currentTarget as HTMLButtonElement).style.backgroundColor =
                              'rgba(30,215,96,0.10)')
                          }
                        >
                          <CheckIcon style={{ width: '14px', height: '14px' }} />
                          Approve
                        </button>
                        <button
                          onClick={() => denyRequest.mutate(req.id)}
                          disabled={approveRequest.isPending || denyRequest.isPending}
                          title="Deny"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            padding: '7px 14px',
                            borderRadius: '8px',
                            border: '1px solid rgba(240,93,94,0.25)',
                            backgroundColor: 'rgba(240,93,94,0.08)',
                            color: 'var(--color-danger)',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'background-color 120ms ease',
                          }}
                          onMouseEnter={(e) =>
                            ((e.currentTarget as HTMLButtonElement).style.backgroundColor =
                              'rgba(240,93,94,0.14)')
                          }
                          onMouseLeave={(e) =>
                            ((e.currentTarget as HTMLButtonElement).style.backgroundColor =
                              'rgba(240,93,94,0.08)')
                          }
                        >
                          <XMarkIcon style={{ width: '14px', height: '14px' }} />
                          Deny
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pending invitations panel — only shown when there is at least one */}
            {pendingInvitations.length > 0 && (
              <div
                style={{
                  backgroundColor: 'var(--color-dark-card)',
                  border: '1px solid var(--color-border-subtle)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: 'var(--shadow-card)',
                }}
              >
                {/* Section header */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '16px 20px',
                    borderBottom: '1px solid var(--color-border-subtle)',
                    backgroundColor: 'var(--color-dark-elevated-1)',
                  }}
                >
                  <ClockIcon style={{ width: '16px', height: '16px', color: 'var(--color-text-muted)' }} />
                  <h2
                    style={{
                      fontFamily: 'var(--font-heading)',
                      fontWeight: 600,
                      fontSize: '15px',
                      color: 'var(--color-text-primary)',
                      margin: 0,
                    }}
                  >
                    Pending Invitations
                  </h2>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '22px',
                      height: '22px',
                      borderRadius: '999px',
                      backgroundColor: 'rgba(61,169,252,0.14)',
                      border: '1px solid rgba(61,169,252,0.28)',
                      color: 'var(--color-accent-blue)',
                      fontSize: '11px',
                      fontWeight: 700,
                    }}
                  >
                    {pendingInvitations.length}
                  </span>
                </div>

                {/* Invitation rows */}
                <div style={{ padding: '8px 0' }}>
                  {pendingInvitations.map((inv) => {
                    const sentDate = formatDate(inv.created_at);
                    const expiresDate = formatDate(inv.expires_at);
                    const daysUntilExpiry = Math.max(
                      0,
                      Math.ceil(
                        (new Date(inv.expires_at).getTime() - Date.now()) /
                          (1000 * 60 * 60 * 24),
                      ),
                    );

                    return (
                      <div
                        key={inv.invitation_id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 20px',
                          gap: '12px',
                          borderBottom: '1px solid var(--color-border-subtle)',
                        }}
                      >
                        {/* Invitation info */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p
                            style={{
                              margin: 0,
                              fontSize: '14px',
                              fontWeight: 500,
                              color: 'var(--color-text-primary)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {inv.email}
                          </p>
                          <p
                            style={{
                              margin: '3px 0 0',
                              fontSize: '12px',
                              color: 'var(--color-text-muted)',
                            }}
                          >
                            Sent {sentDate} &middot;{' '}
                            {daysUntilExpiry > 0
                              ? `Expires in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'} (${expiresDate})`
                              : `Expired ${expiresDate}`}
                          </p>
                        </div>

                        {/* Action buttons row */}
                        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                          {/* Re-send button */}
                          {resendConfirmId === inv.invitation_id ? (
                            // Confirm popover: yes / cancel
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '4px 8px',
                                borderRadius: '8px',
                                border: '1px solid var(--color-border-subtle)',
                                backgroundColor: 'var(--color-surface-raised)',
                                fontSize: '12px',
                              }}
                            >
                              <span style={{ color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                                Send fresh invite to {inv.email.split('@')[0]}?
                              </span>
                              <button
                                onClick={() => void handleResendInvitation(inv.invitation_id)}
                                disabled={resendingInvitationId === inv.invitation_id}
                                style={{
                                  padding: '3px 8px',
                                  borderRadius: '6px',
                                  border: 'none',
                                  backgroundColor: 'var(--color-brand-primary)',
                                  color: 'var(--color-text-inverse)',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                }}
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => setResendConfirmId(null)}
                                style={{
                                  padding: '3px 8px',
                                  borderRadius: '6px',
                                  border: '1px solid var(--color-border-subtle)',
                                  backgroundColor: 'transparent',
                                  color: 'var(--color-text-muted)',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setResendConfirmId(inv.invitation_id)}
                              disabled={resendingInvitationId === inv.invitation_id}
                              title="Re-send invitation with a new link"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px',
                                padding: '6px 12px',
                                borderRadius: '8px',
                                border: '1px solid rgba(59,130,246,0.25)',
                                backgroundColor: 'rgba(59,130,246,0.08)',
                                color: 'var(--color-brand-primary)',
                                fontSize: '12px',
                                fontWeight: 600,
                                cursor: resendingInvitationId === inv.invitation_id ? 'not-allowed' : 'pointer',
                                opacity: resendingInvitationId === inv.invitation_id ? 0.6 : 1,
                                transition: 'background-color 120ms ease',
                              }}
                            >
                              {resendingInvitationId === inv.invitation_id ? (
                                <span
                                  style={{
                                    width: '11px',
                                    height: '11px',
                                    border: '2px solid rgba(59,130,246,0.3)',
                                    borderTopColor: 'var(--color-brand-primary)',
                                    borderRadius: '50%',
                                    display: 'inline-block',
                                    animation: 'spin 0.7s linear infinite',
                                  }}
                                />
                              ) : (
                                <EnvelopeIcon style={{ width: '12px', height: '12px' }} />
                              )}
                              Re-send
                            </button>
                          )}

                          {/* Revoke button */}
                          <button
                            onClick={() => handleRevokeInvitation(inv.invitation_id)}
                            disabled={revokingInvitationId === inv.invitation_id}
                            title="Revoke invitation"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px',
                              padding: '6px 12px',
                              borderRadius: '8px',
                              border: '1px solid rgba(240,93,94,0.25)',
                              backgroundColor: 'rgba(240,93,94,0.08)',
                              color: 'var(--color-danger)',
                              fontSize: '12px',
                              fontWeight: 600,
                              cursor:
                                revokingInvitationId === inv.invitation_id
                                  ? 'not-allowed'
                                  : 'pointer',
                              opacity: revokingInvitationId === inv.invitation_id ? 0.6 : 1,
                              flexShrink: 0,
                              transition: 'background-color 120ms ease',
                            }}
                            onMouseEnter={(e) => {
                              if (revokingInvitationId !== inv.invitation_id) {
                                (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                                  'rgba(240,93,94,0.14)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                                'rgba(240,93,94,0.08)';
                            }}
                          >
                            {revokingInvitationId === inv.invitation_id ? (
                              <span
                                style={{
                                  width: '11px',
                                  height: '11px',
                                  border: '2px solid rgba(240,93,94,0.3)',
                                  borderTopColor: 'var(--color-danger)',
                                  borderRadius: '50%',
                                  display: 'inline-block',
                                  animation: 'spin 0.7s linear infinite',
                                }}
                              />
                            ) : (
                              <XMarkIcon style={{ width: '12px', height: '12px' }} />
                            )}
                            Revoke
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Summary stats row ─────────────────────────────────── */}
            {!isLoadingMembers && !membersError && members.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  backgroundColor: 'var(--color-dark-card)',
                  border: '1px solid var(--color-border-subtle)',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: 'var(--shadow-card)',
                }}
              >
                <div className="cm-stat-mini">
                  <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Total Members
                  </span>
                  <span style={{ fontSize: '22px', fontFamily: 'var(--font-heading)', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                    {members.length}
                  </span>
                </div>
                <div className="cm-stat-mini">
                  <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Avg Latest Score
                  </span>
                  <span style={{ fontSize: '22px', fontFamily: 'var(--font-heading)', fontWeight: 700, color: avgScore != null ? 'var(--color-accent)' : 'var(--color-text-muted)' }}>
                    {avgScore != null ? avgScore : '—'}
                  </span>
                </div>
                <div className="cm-stat-mini">
                  <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    At Risk (&lt;60)
                  </span>
                  <span style={{ fontSize: '22px', fontFamily: 'var(--font-heading)', fontWeight: 700, color: atRiskCount > 0 ? 'var(--color-accent-red)' : 'var(--color-text-primary)' }}>
                    {atRiskCount}
                  </span>
                </div>
              </div>
            )}

            {/* ── Filter chips ──────────────────────────────────────── */}
            {!isLoadingMembers && !membersError && members.length > 0 && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                {(
                  [
                    { key: 'all', label: 'All members' },
                    { key: 'active', label: 'Active this month' },
                    { key: 'inactive', label: 'No evals yet' },
                  ] as { key: MemberFilter; label: string }[]
                ).map((chip) => (
                  <button
                    key={chip.key}
                    onClick={() => setMemberFilter(chip.key)}
                    className={`cm-filter-chip${memberFilter === chip.key ? ' active' : ''}`}
                  >
                    {chip.label}
                    {chip.key === 'all' && ` (${members.length})`}
                    {chip.key === 'active' &&
                      ` (${members.filter((m) => m.last_eval_at != null && new Date(m.last_eval_at).getTime() >= thirtyDaysAgo).length})`}
                    {chip.key === 'inactive' &&
                      ` (${members.filter((m) => (m.session_count ?? 0) === 0).length})`}
                  </button>
                ))}
                {/* B-2026-0068: Coaches-only toggle — hidden for solo (T1) or unknown-tier channels */}
                {seatLimit !== 1 && seatLimit !== null && (
                  <button
                    data-testid="coaches-only-toggle"
                    onClick={() => setShowCoachesOnly((v) => !v)}
                    className={`cm-filter-chip${showCoachesOnly ? ' active' : ''}`}
                  >
                    Coaches only
                  </button>
                )}
              </div>
            )}

            {/* ── Member list ───────────────────────────────────────── */}
            <div
              style={{
                backgroundColor: 'var(--color-dark-card)',
                border: '1px solid var(--color-border-subtle)',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: 'var(--shadow-card)',
              }}
            >
              {/* Table header */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(180px, 1fr) auto auto auto auto auto auto auto',
                  gap: '16px',
                  padding: '12px 20px',
                  borderBottom: '1px solid var(--color-border-subtle)',
                  backgroundColor: 'var(--color-dark-elevated-1)',
                  alignItems: 'center',
                }}
              >
                {/* Member */}
                <button
                  className={`cm-sort-btn${memberSort.column === 'name' ? ' active' : ''}`}
                  onClick={() => handleSortClick('name')}
                >
                  Member
                  {memberSort.column === 'name' ? (
                    memberSort.direction === 'asc'
                      ? <ChevronUpIcon style={{ width: '11px', height: '11px' }} />
                      : <ChevronDownIcon style={{ width: '11px', height: '11px' }} />
                  ) : null}
                </button>
                {/* Role */}
                <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                  Role
                </span>
                {/* Joined */}
                <button
                  className={`cm-sort-btn${memberSort.column === 'joined_at' ? ' active' : ''}`}
                  onClick={() => handleSortClick('joined_at')}
                >
                  Joined
                  {memberSort.column === 'joined_at' ? (
                    memberSort.direction === 'asc'
                      ? <ChevronUpIcon style={{ width: '11px', height: '11px' }} />
                      : <ChevronDownIcon style={{ width: '11px', height: '11px' }} />
                  ) : null}
                </button>
                {/* Score */}
                <button
                  className={`cm-sort-btn${memberSort.column === 'latest_score' ? ' active' : ''}`}
                  onClick={() => handleSortClick('latest_score')}
                >
                  Score
                  {memberSort.column === 'latest_score' ? (
                    memberSort.direction === 'asc'
                      ? <ChevronUpIcon style={{ width: '11px', height: '11px' }} />
                      : <ChevronDownIcon style={{ width: '11px', height: '11px' }} />
                  ) : null}
                </button>
                {/* Trend */}
                <span className="cm-col-trend" style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                  Trend
                </span>
                {/* Sessions */}
                <button
                  className={`cm-sort-btn cm-col-sessions${memberSort.column === 'session_count' ? ' active' : ''}`}
                  onClick={() => handleSortClick('session_count')}
                >
                  Sessions
                  {memberSort.column === 'session_count' ? (
                    memberSort.direction === 'asc'
                      ? <ChevronUpIcon style={{ width: '11px', height: '11px' }} />
                      : <ChevronDownIcon style={{ width: '11px', height: '11px' }} />
                  ) : null}
                </button>
                {/* Last eval */}
                <button
                  className={`cm-sort-btn cm-col-last${memberSort.column === 'last_eval_at' ? ' active' : ''}`}
                  onClick={() => handleSortClick('last_eval_at')}
                >
                  Last
                  {memberSort.column === 'last_eval_at' ? (
                    memberSort.direction === 'asc'
                      ? <ChevronUpIcon style={{ width: '11px', height: '11px' }} />
                      : <ChevronDownIcon style={{ width: '11px', height: '11px' }} />
                  ) : null}
                </button>
                {/* Remove placeholder */}
                <span />
              </div>

              {isLoadingMembers ? (
                <div style={{ padding: '40px', display: 'flex', justifyContent: 'center' }}>
                  <Spinner size="md" />
                </div>
              ) : membersError ? (
                <p
                  style={{
                    padding: '24px',
                    textAlign: 'center',
                    color: 'var(--color-danger)',
                    fontSize: '14px',
                  }}
                >
                  {membersError}
                </p>
              ) : members.length === 0 ? (
                /* Empty state */
                <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                  <div
                    style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '14px',
                      backgroundColor: 'var(--color-dark-elevated-2)',
                      border: '1px solid var(--color-border-subtle)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                    }}
                  >
                    <UserGroupIcon
                      style={{
                        width: '24px',
                        height: '24px',
                        color: 'var(--color-text-muted)',
                      }}
                    />
                  </div>
                  <p
                    style={{
                      fontFamily: 'var(--font-heading)',
                      fontWeight: 600,
                      fontSize: '15px',
                      color: 'var(--color-text-primary)',
                      margin: '0 0 6px',
                    }}
                  >
                    No members yet
                  </p>
                  <p
                    style={{
                      fontSize: '13px',
                      color: 'var(--color-text-muted)',
                      margin: 0,
                    }}
                  >
                    Add your first athlete using the form above.
                  </p>
                </div>
              ) : displayedMembers.length === 0 ? (
                /* Filter empty state */
                <div style={{ padding: '32px 24px', textAlign: 'center' }}>
                  <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', margin: 0 }}>
                    No members match this filter.
                  </p>
                </div>
              ) : (
                /* Member rows */
                <div style={{ padding: '8px 0' }}>
                  {displayedMembers.map((member) => {
                    // Adapt ChannelMember to PerTemplateAthleteRow shape for TrendCell
                    const trendRow = {
                      user_id: member.user_id,
                      display_name: member.display_name,
                      email: member.email,
                      last_score: member.latest_score ?? null,
                      last_eval_at: member.last_eval_at ?? '',
                      personal_best: null,
                      first_score: member.first_score ?? null,
                      first_eval_at: '',
                      session_count: member.session_count ?? 0,
                    };
                    // Phase C — determine if this member is coach-level for T3 quota widget
                    const memberEffectiveRole = (member.member_role ?? member.role) as string;
                    const isCoachLevelForQuota =
                      memberEffectiveRole === 'coach' ||
                      memberEffectiveRole === 'channel_coach' ||
                      memberEffectiveRole === 'channel_admin' ||
                      member.access_level === 'owner';
                    // Quota widget is owner-only (not delegated to channel_admin)
                    const showQuotaWidget = isOwner && channel.subscription_tier === 3 && isCoachLevelForQuota;
                    // D-S22 owner-immutability: whether THIS MEMBER ROW is the channel owner.
                    // Remove button is hidden on the owner row for ALL viewers (immutable).
                    // S56: viewerCanRemove (owner OR explicit can_remove_members grant) gates Remove.
                    const isMemberOwnerRow =
                      member.access_level === 'owner' ||
                      member.user_id === channel.owner_id ||
                      memberEffectiveRole === 'owner';
                    // S56: owner OR a member with can_remove_members sees Remove on non-owner rows.
                    // NEVER show Remove on the owner row for any viewer.
                    const showRemoveButton = viewerCanRemove && !isMemberOwnerRow;
                    // S56: capability toggles are owner-only (+ platform admin — not delegated).
                    // Non-owner viewers (even with can_remove_members) cannot manage capabilities.
                    const showCapabilityToggles = isOwner && !isMemberOwnerRow;
                    const quotaState = coachQuotas[member.user_id];
                    const quotaEditorState = coachQuotaEditing[member.user_id];
                    return (
                      <div key={member.user_id}>
                      <div
                        className="cm-member-row"
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(180px, 1fr) auto auto auto auto auto auto auto',
                          gap: '16px',
                          alignItems: 'center',
                        }}
                      >
                        {/* Name / email */}
                        <div style={{ minWidth: 0 }}>
                          <p
                            style={{
                              margin: 0,
                              fontSize: '14px',
                              fontWeight: 500,
                              color: 'var(--color-text-primary)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {member.display_name || member.email}
                          </p>
                          {member.display_name && (
                            <p
                              style={{
                                margin: '2px 0 0',
                                fontSize: '12px',
                                color: 'var(--color-text-muted)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {member.email}
                            </p>
                          )}
                        </div>

                        {/* Role badge + change control (B-2026-0064/0068) */}
                        {(() => {
                          const effectiveRole = (member.member_role ?? member.role) as string;
                          // isMemberOwner: whether THIS MEMBER ROW is the channel owner.
                          // Distinct from `isOwner` (outer scope: whether the VIEWER is the owner).
                          const isMemberOwner =
                            member.access_level === 'owner' ||
                            member.user_id === channel.owner_id ||
                            (member.member_role ?? member.role) === 'owner';
                          const isCoach = effectiveRole === 'coach' || effectiveRole === 'channel_coach' || effectiveRole === 'channel_admin';
                          const displayRole = isMemberOwner ? 'Owner' : isCoach ? 'Coach' : effectiveRole.charAt(0).toUpperCase() + effectiveRole.slice(1);
                          const roleColor = isMemberOwner ? 'var(--color-accent-gold)' : isCoach ? 'var(--color-accent-blue)' : 'var(--color-accent)';
                          const roleBg = isMemberOwner ? 'rgba(244,201,93,0.12)' : isCoach ? 'rgba(61,169,252,0.12)' : 'rgba(30,215,96,0.10)';
                          const roleBorder = isMemberOwner ? 'rgba(244,201,93,0.22)' : isCoach ? 'rgba(61,169,252,0.22)' : 'rgba(30,215,96,0.18)';

                          // Owner row: static badge, no change control (mirrors backend cannot_change_owner_role).
                          if (isMemberOwner) {
                            return (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 9px', borderRadius: '999px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap', backgroundColor: roleBg, border: `1px solid ${roleBorder}`, color: roleColor }}>
                                {displayRole}
                              </span>
                            );
                          }

                          // Non-owner row: badge + optional role-change dropdown.
                          // Capability discipline: the role-change select is owner-only —
                          // change_member_role is not delegated to channel_admin at launch.
                          const currentSelectValue: 'athlete' | 'coach' | 'viewer' =
                            isCoach ? 'coach' : effectiveRole === 'viewer' ? 'viewer' : 'athlete';
                          const isChanging = changingRoleUserId === member.user_id;

                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 9px', borderRadius: '999px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap', backgroundColor: roleBg, border: `1px solid ${roleBorder}`, color: roleColor }}>
                                {displayRole}
                              </span>
                              {/* isOwner here is the outer-scope viewer ownership flag, not the member row flag */}
                              {isOwner && (
                                <>
                                  <select
                                    aria-label={`Change role for ${member.display_name ?? member.email}`}
                                    value={currentSelectValue}
                                    disabled={isChanging || isReadOnly}
                                    onChange={(e) => void handleRoleChange(member.user_id, e.target.value as 'athlete' | 'coach' | 'viewer')}
                                    style={{
                                      fontSize: '11px',
                                      padding: '2px 4px',
                                      borderRadius: '6px',
                                      border: '1px solid var(--color-border-subtle)',
                                      backgroundColor: 'var(--color-dark-elevated-2)',
                                      color: 'var(--color-text-muted)',
                                      cursor: isChanging || isReadOnly ? 'not-allowed' : 'pointer',
                                      opacity: isChanging ? 0.6 : 1,
                                    }}
                                  >
                                    <option value="athlete">Athlete</option>
                                    <option value="coach">Coach</option>
                                    <option value="viewer">Viewer</option>
                                  </select>
                                  {isChanging && (
                                    <span style={{ width: '11px', height: '11px', border: '1.5px solid var(--color-border-strong)', borderTopColor: 'var(--color-accent)', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite', flexShrink: 0 }} />
                                  )}
                                </>
                              )}
                            </div>
                          );
                        })()}

                        {/* Joined date */}
                        <span
                          style={{
                            fontSize: '13px',
                            color: 'var(--color-text-muted)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatDate(member.joined_at)}
                        </span>

                        {/* Score */}
                        <ScoreCell value={member.latest_score ?? null} />

                        {/* Trend */}
                        <span className="cm-col-trend">
                          <TrendCell row={trendRow} />
                        </span>

                        {/* Sessions */}
                        <span className="cm-col-sessions" style={{ fontSize: '13px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                          {(member.session_count ?? 0) > 0 ? member.session_count : '—'}
                        </span>

                        {/* Last eval */}
                        <span className="cm-col-last" style={{ fontSize: '13px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                          {member.last_eval_at
                            ? formatDistanceToNow(new Date(member.last_eval_at), { addSuffix: true })
                            : '—'}
                        </span>

                        {/* Evaluate quick-link */}
                        <button
                          className="cm-remove-btn"
                          onClick={() => navigate('/evaluate')}
                          title="Run evaluation for this athlete"
                          aria-label={`Run evaluation for ${member.display_name ?? member.email}`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            border: '1px solid transparent',
                            backgroundColor: 'transparent',
                            color: 'var(--color-text-muted)',
                            cursor: 'pointer',
                            transition: 'background-color 150ms ease, color 150ms ease, border-color 150ms ease',
                          }}
                          onMouseEnter={(e) => {
                            const btn = e.currentTarget as HTMLButtonElement;
                            btn.style.backgroundColor = 'rgba(99,102,241,0.10)';
                            btn.style.color = 'var(--color-accent)';
                            btn.style.borderColor = 'rgba(99,102,241,0.25)';
                          }}
                          onMouseLeave={(e) => {
                            const btn = e.currentTarget as HTMLButtonElement;
                            btn.style.backgroundColor = 'transparent';
                            btn.style.color = 'var(--color-text-muted)';
                            btn.style.borderColor = 'transparent';
                          }}
                        >
                          <VideoCameraIcon style={{ width: '15px', height: '15px' }} />
                        </button>

                        {/* Remove — owner-only; hidden on the owner row and for channel_admin viewers.
                            D-S22: isMemberOwnerRow guard closes the gap where Remove rendered
                            unconditionally (mirrors backend cannot_remove_owner). */}
                        {showRemoveButton ? (
                          <button
                            className="cm-remove-btn"
                            onClick={() => handleRemoveMember(member.user_id)}
                            disabled={removingMemberId === member.user_id}
                            title="Remove member"
                            aria-label="Remove member"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '32px',
                              height: '32px',
                              borderRadius: '8px',
                              border: '1px solid transparent',
                              backgroundColor: 'transparent',
                              color: 'var(--color-text-muted)',
                              cursor:
                                removingMemberId === member.user_id
                                  ? 'not-allowed'
                                  : 'pointer',
                              transition: 'background-color 150ms ease, color 150ms ease, border-color 150ms ease',
                            }}
                            onMouseEnter={(e) => {
                              const btn = e.currentTarget as HTMLButtonElement;
                              btn.style.backgroundColor = 'rgba(240,93,94,0.10)';
                              btn.style.color = 'var(--color-danger)';
                              btn.style.borderColor = 'rgba(240,93,94,0.25)';
                            }}
                            onMouseLeave={(e) => {
                              const btn = e.currentTarget as HTMLButtonElement;
                              btn.style.backgroundColor = 'transparent';
                              btn.style.color = 'var(--color-text-muted)';
                              btn.style.borderColor = 'transparent';
                            }}
                          >
                            {removingMemberId === member.user_id ? (
                              <span
                                style={{
                                  width: '13px',
                                  height: '13px',
                                  border: '2px solid rgba(240,93,94,0.3)',
                                  borderTopColor: 'var(--color-danger)',
                                  borderRadius: '50%',
                                  display: 'inline-block',
                                  animation: 'spin 0.7s linear infinite',
                                }}
                              />
                            ) : (
                              <TrashIcon style={{ width: '15px', height: '15px' }} />
                            )}
                          </button>
                        ) : (
                          // Placeholder to keep the grid column alignment consistent
                          <span style={{ width: '32px', height: '32px', display: 'inline-block' }} />
                        )}
                      </div>

                      {/* S56 — Capability toggles (owner viewer only, non-owner rows only) */}
                      {showCapabilityToggles && (() => {
                        const toggleLoadingState = capabilityToggleState[member.user_id];
                        const isToggling = toggleLoadingState?.loading === true;
                        const toggleError = toggleLoadingState?.error ?? null;
                        const canRemoveValue = member.can_remove_members === true;
                        return (
                          <div
                            data-testid={`capability-panel-${member.user_id}`}
                            style={{
                              margin: '0 16px 8px 52px',
                              padding: '10px 14px',
                              borderRadius: '10px',
                              backgroundColor: 'var(--color-dark-elevated-2)',
                              border: '1px solid var(--color-border-subtle)',
                            }}
                          >
                            <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Member Capabilities
                            </p>

                            {/* can_remove_members toggle */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                role="switch"
                                aria-checked={canRemoveValue}
                                aria-label={`Toggle can remove members for ${member.display_name ?? member.email}`}
                                data-testid={`can-remove-toggle-${member.user_id}`}
                                disabled={isToggling || isReadOnly}
                                onClick={() => void handleToggleRemovalCapability(member.user_id, canRemoveValue)}
                                style={{
                                  flexShrink: 0,
                                  width: '36px',
                                  height: '20px',
                                  borderRadius: '999px',
                                  border: 'none',
                                  cursor: isToggling || isReadOnly ? 'not-allowed' : 'pointer',
                                  backgroundColor: canRemoveValue ? 'var(--color-accent)' : 'var(--color-border-strong)',
                                  position: 'relative',
                                  transition: 'background-color 180ms ease',
                                  opacity: isToggling ? 0.6 : 1,
                                }}
                              >
                                <span
                                  style={{
                                    position: 'absolute',
                                    top: '3px',
                                    left: canRemoveValue ? '19px' : '3px',
                                    width: '14px',
                                    height: '14px',
                                    borderRadius: '50%',
                                    backgroundColor: 'white',
                                    transition: 'left 180ms ease',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                                  }}
                                />
                                <span className="sr-only">
                                  {canRemoveValue ? 'Revoke' : 'Grant'} remove-members capability
                                </span>
                              </button>
                              <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                                Can remove members
                              </span>
                              {isToggling && (
                                <span
                                  style={{
                                    width: '11px',
                                    height: '11px',
                                    border: '1.5px solid var(--color-border-strong)',
                                    borderTopColor: 'var(--color-accent)',
                                    borderRadius: '50%',
                                    display: 'inline-block',
                                    animation: 'spin 0.7s linear infinite',
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                              {/* TODO S56: can_authorize_purchases toggle — wire to existing
                                  purchase-authorization endpoint (credit_routes.py:set_purchase_authorization)
                                  once a frontend service function (setPurchaseAuthorization) exists in
                                  channelApi.ts or creditApi.ts. Do NOT implement here without the endpoint. */}
                            </div>

                            {/* Inline error for capability toggle */}
                            {toggleError && (
                              <p
                                role="alert"
                                data-testid={`capability-error-${member.user_id}`}
                                style={{
                                  margin: '6px 0 0',
                                  fontSize: '12px',
                                  color: 'var(--color-danger)',
                                }}
                              >
                                {toggleError}
                              </p>
                            )}
                          </div>
                        );
                      })()}

                      {/* Phase C — Per-coach quota widget (T3 only) */}
                      {showQuotaWidget && (
                        <div
                          data-testid={`quota-widget-${member.user_id}`}
                          style={{
                            margin: '0 16px 8px 52px',
                            padding: '10px 14px',
                            borderRadius: '10px',
                            backgroundColor: 'var(--color-dark-elevated-2)',
                            border: '1px solid var(--color-border-subtle)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            flexWrap: 'wrap',
                          }}
                        >
                          <AdjustmentsHorizontalIcon style={{ width: '14px', height: '14px', color: 'var(--color-text-muted)', flexShrink: 0 }} />
                          <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', fontWeight: 500, flexShrink: 0 }}>
                            Monthly template limit:
                          </span>

                          {quotaEditorState?.editing ? (
                            /* Inline editor */
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', flex: 1 }}>
                              <input
                                type="number"
                                min={0}
                                value={quotaEditorState.value}
                                onChange={(e) =>
                                  setCoachQuotaEditing((prev) => ({
                                    ...prev,
                                    [member.user_id]: { ...quotaEditorState, value: e.target.value },
                                  }))
                                }
                                disabled={quotaEditorState.saving}
                                aria-label={`Set monthly template limit for ${member.display_name ?? member.email}`}
                                data-testid={`quota-input-${member.user_id}`}
                                style={{
                                  width: '72px',
                                  height: '28px',
                                  padding: '0 8px',
                                  borderRadius: '6px',
                                  border: '1px solid var(--color-border-strong)',
                                  backgroundColor: 'var(--color-dark-card)',
                                  color: 'var(--color-text-primary)',
                                  fontSize: '13px',
                                  outline: 'none',
                                }}
                              />
                              <button
                                onClick={() => void saveCoachQuota(member.user_id)}
                                disabled={quotaEditorState.saving || isReadOnly}
                                data-testid={`quota-save-${member.user_id}`}
                                style={{
                                  height: '28px',
                                  padding: '0 12px',
                                  borderRadius: '6px',
                                  backgroundColor: 'var(--color-accent)',
                                  border: 'none',
                                  color: 'var(--color-text-inverse)',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  cursor: quotaEditorState.saving || isReadOnly ? 'not-allowed' : 'pointer',
                                  opacity: quotaEditorState.saving ? 0.6 : 1,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}
                              >
                                {quotaEditorState.saving ? (
                                  <span style={{ width: '10px', height: '10px', border: '1.5px solid rgba(0,0,0,0.2)', borderTopColor: 'rgba(0,0,0,0.6)', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                                ) : (
                                  <CheckIcon style={{ width: '12px', height: '12px' }} />
                                )}
                                Save
                              </button>
                              <button
                                onClick={() => cancelQuotaEditor(member.user_id)}
                                disabled={quotaEditorState.saving}
                                style={{
                                  height: '28px',
                                  padding: '0 10px',
                                  borderRadius: '6px',
                                  backgroundColor: 'transparent',
                                  border: '1px solid var(--color-border-subtle)',
                                  color: 'var(--color-text-muted)',
                                  fontSize: '12px',
                                  cursor: quotaEditorState.saving ? 'not-allowed' : 'pointer',
                                }}
                              >
                                Cancel
                              </button>
                              {quotaEditorState.error && (
                                <span style={{ fontSize: '12px', color: 'var(--color-danger)' }}>
                                  {quotaEditorState.error}
                                </span>
                              )}
                            </div>
                          ) : (
                            /* Display state */
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, flexWrap: 'wrap' }}>
                              {(quotaState === 'loading' || quotaState === undefined) ? (
                                <span style={{ width: '12px', height: '12px', border: '1.5px solid var(--color-border-strong)', borderTopColor: 'var(--color-accent-blue)', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                              ) : quotaState === 'error' ? (
                                <span style={{ fontSize: '12px', color: 'var(--color-danger)' }}>Failed to load</span>
                              ) : quotaState === null ? (
                                <span
                                  data-testid={`quota-default-${member.user_id}`}
                                  style={{ fontSize: '12px', color: 'var(--color-text-muted)', fontStyle: 'italic' }}
                                >
                                  Platform default (no limit)
                                </span>
                              ) : (
                                <span data-testid={`quota-usage-${member.user_id}`} style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                                  <span style={{ fontWeight: 600, color: quotaState.current_month_used >= quotaState.templates_created_per_month && quotaState.templates_created_per_month > 0 ? 'var(--color-danger)' : 'var(--color-text-primary)' }}>
                                    {quotaState.current_month_used}
                                  </span>
                                  {' / '}
                                  <span style={{ fontWeight: 600 }}>
                                    {quotaState.templates_created_per_month === 0 ? 'Blocked' : quotaState.templates_created_per_month}
                                  </span>
                                  {quotaState.templates_created_per_month > 0 && ' this month'}
                                </span>
                              )}

                              {/* Set/Edit and Clear buttons — only when quota is resolved (null or CoachQuotaResponse) */}
                              {(quotaState === null || (quotaState !== 'loading' && quotaState !== undefined && quotaState !== 'error')) && (
                                <>
                                  <button
                                    onClick={() => openQuotaEditor(member.user_id)}
                                    disabled={isReadOnly}
                                    data-testid={`quota-edit-${member.user_id}`}
                                    style={{
                                      height: '24px',
                                      padding: '0 9px',
                                      borderRadius: '6px',
                                      backgroundColor: 'transparent',
                                      border: '1px solid var(--color-border-subtle)',
                                      color: 'var(--color-text-muted)',
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      cursor: isReadOnly ? 'not-allowed' : 'pointer',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      transition: 'color 120ms ease, border-color 120ms ease',
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isReadOnly) {
                                        (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-accent-blue)';
                                        (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(61,169,252,0.40)';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-muted)';
                                      (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-border-subtle)';
                                    }}
                                  >
                                    <PencilSquareIcon style={{ width: '11px', height: '11px' }} />
                                    {quotaState === null ? 'Set limit' : 'Edit'}
                                  </button>

                                  {quotaState !== null && (
                                    <button
                                      onClick={() => void clearCoachQuota(member.user_id)}
                                      disabled={isReadOnly}
                                      data-testid={`quota-clear-${member.user_id}`}
                                      style={{
                                        height: '24px',
                                        padding: '0 9px',
                                        borderRadius: '6px',
                                        backgroundColor: 'transparent',
                                        border: '1px solid rgba(240,93,94,0.25)',
                                        color: 'var(--color-danger)',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        cursor: isReadOnly ? 'not-allowed' : 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        opacity: isReadOnly ? 0.5 : 1,
                                        transition: 'background-color 120ms ease',
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!isReadOnly) (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(240,93,94,0.08)';
                                      }}
                                      onMouseLeave={(e) => {
                                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                                      }}
                                    >
                                      <NoSymbolIcon style={{ width: '11px', height: '11px' }} />
                                      Remove limit
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Templates Tab ──────────────────────────────────────────── */}
        {activeTab === 'templates' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* Coach filter */}
            <div
              style={{
                backgroundColor: 'var(--color-dark-card)',
                border: '1px solid var(--color-border-subtle)',
                borderRadius: '16px',
                padding: '20px 24px',
                boxShadow: 'var(--shadow-card)',
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                flexWrap: 'wrap',
              }}
            >
              <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-text-secondary)', flexShrink: 0 }}>
                Filter by coach:
              </span>
              <select
                value={templateCoachFilter}
                onChange={(e) => setTemplateCoachFilter(e.target.value)}
                aria-label="Filter templates by coach"
                data-testid="template-coach-filter"
                style={{
                  height: '36px',
                  padding: '0 30px 0 12px',
                  borderRadius: '10px',
                  border: '1px solid var(--color-border-strong)',
                  backgroundColor: 'var(--color-dark-elevated-2)',
                  color: 'var(--color-text-primary)',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  appearance: 'none',
                  backgroundImage:
                    'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3E%3Cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3E%3C/svg%3E")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 8px center',
                  backgroundSize: '16px',
                  minWidth: '180px',
                }}
              >
                <option value="">All coaches</option>
                {coachLevelMembersForFilter.map((coach) => (
                  <option key={coach.user_id} value={coach.user_id}>
                    {coach.display_name ?? coach.email}
                  </option>
                ))}
              </select>
            </div>

            {/* Templates list */}
            <div
              style={{
                backgroundColor: 'var(--color-dark-card)',
                border: '1px solid var(--color-border-subtle)',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: 'var(--shadow-card)',
              }}
            >
              {/* Table header */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(200px, 1fr) auto auto auto',
                  gap: '16px',
                  padding: '12px 20px',
                  borderBottom: '1px solid var(--color-border-subtle)',
                  backgroundColor: 'var(--color-dark-elevated-1)',
                  alignItems: 'center',
                }}
              >
                <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                  Template
                </span>
                <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                  Coach
                </span>
                <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                  Visibility
                </span>
                <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                  Created
                </span>
              </div>

              {isLoadingTemplates ? (
                <div style={{ padding: '40px', display: 'flex', justifyContent: 'center' }}>
                  <Spinner size="md" />
                </div>
              ) : templatesError ? (
                <p
                  role="alert"
                  style={{
                    padding: '24px',
                    textAlign: 'center',
                    color: 'var(--color-danger)',
                    fontSize: '14px',
                  }}
                >
                  {templatesError}
                </p>
              ) : templates.length === 0 ? (
                <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                  <div
                    style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '14px',
                      backgroundColor: 'var(--color-dark-elevated-2)',
                      border: '1px solid var(--color-border-subtle)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                    }}
                  >
                    <RectangleStackIcon style={{ width: '24px', height: '24px', color: 'var(--color-text-muted)' }} />
                  </div>
                  <p style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: '15px', color: 'var(--color-text-primary)', margin: '0 0 6px' }}>
                    {templateCoachFilter ? 'No templates for this coach.' : 'No templates yet.'}
                  </p>
                  <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', margin: 0 }}>
                    {templateCoachFilter ? 'Try selecting a different coach or "All coaches".' : 'Coaches can create templates from the Templates page.'}
                  </p>
                </div>
              ) : (
                <div style={{ padding: '8px 0' }}>
                  {templates.map((tpl) => (
                    <div
                      key={tpl.id}
                      data-testid={`template-row-${tpl.id}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(200px, 1fr) auto auto auto',
                        gap: '16px',
                        padding: '12px 20px',
                        alignItems: 'center',
                        borderRadius: '10px',
                        transition: 'background-color 150ms ease',
                      }}
                      onMouseEnter={(e) =>
                        ((e.currentTarget as HTMLDivElement).style.backgroundColor =
                          'var(--color-dark-elevated-2)')
                      }
                      onMouseLeave={(e) =>
                        ((e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent')
                      }
                    >
                      {/* Name + sport/movement */}
                      <div style={{ minWidth: 0 }}>
                        <p
                          style={{
                            margin: 0,
                            fontSize: '14px',
                            fontWeight: 500,
                            color: 'var(--color-text-primary)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {tpl.name}
                        </p>
                        {(tpl.sport_category || tpl.movement_type) && (
                          <p
                            style={{
                              margin: '2px 0 0',
                              fontSize: '12px',
                              color: 'var(--color-text-muted)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {[tpl.sport_category, tpl.movement_type].filter(Boolean).join(' · ')}
                          </p>
                        )}
                      </div>

                      {/* Coach */}
                      <span style={{ fontSize: '13px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                        {tpl.owner_display_name ?? '—'}
                      </span>

                      {/* Visibility badge */}
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '3px 9px',
                          borderRadius: '999px',
                          fontSize: '11px',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          backgroundColor:
                            tpl.visibility === 'public'
                              ? 'rgba(30,215,96,0.10)'
                              : 'rgba(135,147,163,0.12)',
                          border:
                            tpl.visibility === 'public'
                              ? '1px solid rgba(30,215,96,0.18)'
                              : '1px solid rgba(135,147,163,0.22)',
                          color:
                            tpl.visibility === 'public'
                              ? 'var(--color-accent)'
                              : 'var(--color-text-muted)',
                        }}
                      >
                        {tpl.visibility === 'public' ? 'Public' : 'Private'}
                      </span>

                      {/* Created date */}
                      <span style={{ fontSize: '13px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                        {tpl.created_at ? formatDate(tpl.created_at) : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Credits Tab ──────────────────────────────────────────────── */}
        {activeTab === 'credits' && (
          <ChannelCreditsTab
            slug={slug ?? ''}
            channel={channel}
            members={members}
            isOwner={isOwner}
            isAdmin={isAdmin}
            onToast={showCreditToast}
          />
        )}
      </div>

      {/* Credits tab toast */}
      {creditToast && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 9999,
            padding: '14px 16px',
            borderRadius: '10px',
            backgroundColor:
              creditToast.type === 'error' ? 'rgba(248,113,113,0.12)' : 'rgba(52,211,153,0.12)',
            border: `1px solid ${creditToast.type === 'error' ? 'rgba(248,113,113,0.35)' : 'rgba(52,211,153,0.35)'}`,
            boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
            maxWidth: '420px',
            color: 'var(--color-text-primary)',
            fontSize: '14px',
          }}
        >
          {creditToast.message}
        </div>
      )}
    </>
  );
}
